import React, { useState } from "react";
import "./header.css";
import UserOutlined from "@ant-design/icons/UserOutlined.js";
import { Avatar, Dropdown, Space, Menu, DatePicker, Select, Modal, Switch } from "antd";
import myImage from './fyzks-logo.png';
import { getUserDetails, makePostCall } from "../../utils/helper";
import dayjs from 'dayjs';
import FloatLabel from "../../components/FloatingLabel";
import ChangePassword from "../change-password";
import EditProfile from "../edit-profile";
import { DownloadMenuItem } from "../../components/DownloadMenuItem";
import { TokenStorage, UserStorage } from "../../utils/cookieStorage";
import { sessionManager } from "../../utils/sessionManager";
import { getAutoRefreshEnabled, publishAutoRefresh } from "../../utils/autoRefreshPubSub";
import ReportSearch from "../ReportSearch";
import AcademicsPage from "../academics";
import DeletedStudiesModal from "../../components/DeletedStudiesModal";
import ShortcutSettingsModal from "../../components/ShortcutSettingsModal";
import TemplateListOodocs from "../TemplateListOodocs";

const { RangePicker } = DatePicker;

const AppHeader = ({ handleDateChange, tabsSlot }) => {
  const userId = localStorage.getItem("user_id");
  const userToken = localStorage.getItem("access_token");
  const [changePasswordModal, setChangePasswordModal] = useState({ visible: false });
  const [editProfileModal, setEditProfileModal] = useState({ visible: false });
  const [reportSearchModal, setReportSearchModal] = useState({ visible: false });
  const [academicsModal, setAcademicsModal] = useState({ visible: false });
  const [deletedStudiesModal, setDeletedStudiesModal] = useState({ visible: false });
  const [shortcutSettingsModal, setShortcutSettingsModal] = useState({ visible: false });
  const [templatesModal, setTemplatesModal] = useState({ visible: false });

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());
  const userDetails = getUserDetails();
  const isInstaller = userDetails?.user_type === 'installer';
  // Role shown under the user name; codes that aren't plain words get a
  // dedicated label, the rest are just capitalized (radiologist -> Radiologist).
  const ROLE_LABELS = { hod: 'HOD', frontdesk: 'Front Desk' };
  const roleLabel = userDetails?.user_type
    ? ROLE_LABELS[userDetails.user_type] ||
      userDetails.user_type.charAt(0).toUpperCase() + userDetails.user_type.slice(1)
    : '';
  // Order deletion is gated behind a runtime env flag and limited to radiologists / HOD.
  const canDeleteOrder =
    !!(window as any).ENV_CONFIG?.ENABLE_ORDER_DELETE &&
    ['radiologist', 'hod'].includes(userDetails?.user_type);
  // Academics moved from a header tab into the profile menu — same gating as
  // the old `showAcademics` tab: radiologists / HOD who aren't consultants.
  const canSeeAcademics =
    ['radiologist', 'hod'].includes(userDetails?.user_type) && !userDetails?.consultant;
  // Technicians don't use shortcuts, so hide the Shortcut Settings menu item for them.
  const canSeeShortcutSettings = userDetails?.user_type !== 'technician';
  // Export Tasks (push-to-node status) is only relevant to the roles that push studies.
  const canSeeExportTasks = ['radiologist', 'technician'].includes(userDetails?.user_type);
  // Self-service templates — radiologists, typists, and HODs manage their own (and
  // global) report templates here.
  const canSeeTemplates = ['radiologist', 'typist', 'hod'].includes(userDetails?.user_type);
  if (isInstaller) { return null; }

  const onAutoRefreshToggle = (checked) => {
    publishAutoRefresh(checked);
    setAutoRefreshEnabled(checked);
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to invalidate session in database
      await makePostCall('/logout', {});
    } catch (error) {
      // Even if API call fails, proceed with local cleanup
      console.error('Logout API call failed:', error);
    } finally {
      // Clear all authentication data
      TokenStorage.clearAllTokens();
      UserStorage.removeUserDetails();
      localStorage.clear();
      sessionStorage.clear();

      // Clear session manager
      sessionManager.clearSession();

      window.location.href = "/login";
    }
  }


  const handleChangePassword = () => {
    setChangePasswordModal({ visible: true });
  }

  const handleEditProfile = () => {
    setEditProfileModal({ visible: true });
  }

  const handleReportSearch = () => {
    setReportSearchModal({ visible: true });
  }

  const handleAcademics = () => {
    setAcademicsModal({ visible: true });
  }

  const handleProfileUpdateSuccess = () => {
    // Reload the page to reflect updated user details in the header
    window.location.reload();
  }

  const items = [
    {
      key: "1",
      label: <a onClick={handleEditProfile}>Edit Profile</a>,
    },
    {
      key: "3",
      label: <a onClick={handleReportSearch}>Report Search</a>,
    },
    ...(canSeeExportTasks
      ? [
          {
            key: "export-tasks",
            label: <a href="/export-tasks">Export Tasks</a>,
          },
        ]
      : []),
    ...(canSeeTemplates
      ? [
          {
            key: "templates",
            label: <a onClick={() => setTemplatesModal({ visible: true })}>Templates</a>,
          },
        ]
      : []),
    ...(canSeeAcademics
      ? [
          {
            key: "academics",
            label: <a onClick={handleAcademics}>Academics</a>,
          },
        ]
      : []),
    ...(canSeeShortcutSettings
      ? [
          {
            key: "shortcut-settings",
            label: <a onClick={() => setShortcutSettingsModal({ visible: true })}>Shortcut Settings</a>,
          },
        ]
      : []),
    ...(canDeleteOrder
      ? [
          {
            key: "deleted-studies",
            label: <a onClick={() => setDeletedStudiesModal({ visible: true })}>Deleted Studies</a>,
          },
        ]
      : []),
    {
      key: "4",
      label: <a onClick={handleLogout}>Logout</a>,
    },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: "4",
    //   label: (
    //     <DownloadMenuItem
    //       appType="backend"
    //       onError={(error) => {
    //         console.error('Backend download failed:', error);
    //         // Additional error tracking could be added here
    //       }}
    //       onSuccess={(filename) => {
    //         console.log('Backend files downloaded successfully:', filename);
    //         // Additional success tracking could be added here
    //       }}
    //     >
    //       Download Backend Files
    //     </DownloadMenuItem>
    //   ),
    // },
    // {
    //   key: "5",
    //   label: (
    //     <DownloadMenuItem
    //       appType="listener"
    //       onError={(error) => {
    //         console.error('Listener download failed:', error);
    //         // Additional error tracking could be added here
    //       }}
    //       onSuccess={(filename) => {
    //         console.log('Listener files downloaded successfully:', filename);
    //         // Additional success tracking could be added here
    //       }}
    //     >
    //       Download Listener Files
    //     </DownloadMenuItem>
    //   ),
    // },
  ];

  // Remove settings from dropdown for admin since they now see settings by default

  const rangePresets = [
    { label: 'Today', value: 0 },
    { label: 'Last 2 Days', value: 1 },
    { label: 'Last 7 Days', value: 6 },
    { label: 'Last 30 Days', value: 30 },
  ];

  const onRangeChange = (val) => {
    handleDateChange(val);
    // [dayjs().add(-7, 'd'), dayjs()]
  }


  return (
    <div className="header-container">
      <div className="logo" onClick={() => { window.location.href = "/" }}>
        <div className="flex items-center gap-3 cursor-pointer">
          <div className="logo-dots">
            <div className="w-2 h-2 bg-red-500 rounded-full dot-animation dot-1"></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full dot-animation dot-2"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full dot-animation dot-3"></div>
            <div className="w-2 h-2 bg-yellow-500 rounded-full dot-animation dot-4"></div>
            <div className="w-2 h-2 bg-purple-500 rounded-full dot-animation dot-5"></div>
            <div className="w-2 h-2 bg-pink-500 rounded-full dot-animation dot-6"></div>
            <div className="w-2 h-2 bg-green-500 rounded-full dot-animation dot-7"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full dot-animation dot-8"></div>
          </div>
          <span className="logo-text">Photon</span>
        </div>
      </div>

      {/* Role-based navigation tabs live in the header now (between logo and profile) */}
      {tabsSlot ? <div className="header-tabs">{tabsSlot}</div> : null}

      {/* <RangePicker className="ms-auto me-2" presets={rangePresets} onChange={onRangeChange} /> */}
      {/* {
        userDetails ? <FloatLabel label="Date Range" className="ms-auto mt-auto me-3">
          <Select
            style={{ width: 200 }}
            options={rangePresets}
            onChange={onRangeChange}
          />
        </FloatLabel> : null
      } */}

      <div className="header-right-section">
        {/* Auto Refresh Toggle - only show if handlers are provided */}
        {onAutoRefreshToggle && (
          <div className="auto-refresh-toggle" style={{ marginRight: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '14px', color: '#262626' }}>Auto Sync</span>
            <Switch
              checked={autoRefreshEnabled}
              onChange={onAutoRefreshToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
        )}
        <div className="profile-section">
          {userDetails ? <Avatar size={32} icon={<UserOutlined />} /> : null}
          <Dropdown menu={{ items }} trigger={['click']}>
            <a onClick={(e) => e.preventDefault()} className="profile-name-dropdown">
              <span className="user-identity">
                <span className="user-name">{userDetails?.user_fullname}</span>
                {roleLabel && <span className="user-role">{roleLabel}</span>}
              </span>
              <span className="dropdown-arrow">▼</span>
            </a>
          </Dropdown>
        </div>
      </div>
      {changePasswordModal.visible && (
        <Modal
          title="Change Password"
          open={changePasswordModal.visible}
          width={400}
          onCancel={() => { setChangePasswordModal({ visible: false }) }}
          footer={null}
        >
          <ChangePassword onCancel={() => { setChangePasswordModal({ visible: false }) }} />
        </Modal>
      )}
      {editProfileModal.visible && (
        <EditProfile
          visible={editProfileModal.visible}
          onCancel={() => { setEditProfileModal({ visible: false }) }}
          onSuccess={handleProfileUpdateSuccess}
        />
      )}
      {reportSearchModal.visible && (
        <ReportSearch
          visible={reportSearchModal.visible}
          onCancel={() => { setReportSearchModal({ visible: false }) }}
        />
      )}
      {academicsModal.visible && (
        <Modal
          title="Academics"
          open={academicsModal.visible}
          onCancel={() => { setAcademicsModal({ visible: false }) }}
          footer={null}
          width="90vw"
          style={{ top: 20, maxWidth: 1500 }}
          styles={{ body: { height: '80vh', padding: 0, overflow: 'hidden' } }}
        >
          <AcademicsPage inDialog />
        </Modal>
      )}
      {templatesModal.visible && (
        <Modal
          title="Templates"
          open={templatesModal.visible}
          onCancel={() => { setTemplatesModal({ visible: false }) }}
          footer={null}
          width="90vw"
          style={{ top: 20, maxWidth: 1500 }}
          styles={{ body: { height: '80vh', padding: 0, overflow: 'auto' } }}
        >
          <TemplateListOodocs selfService />
        </Modal>
      )}
      {deletedStudiesModal.visible && (
        <DeletedStudiesModal
          visible={deletedStudiesModal.visible}
          onCancel={() => { setDeletedStudiesModal({ visible: false }) }}
        />
      )}
      {shortcutSettingsModal.visible && (
        <ShortcutSettingsModal
          visible={shortcutSettingsModal.visible}
          onCancel={() => { setShortcutSettingsModal({ visible: false }) }}
        />
      )}
    </div>
  );
};

export default AppHeader;
